import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarSync, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isIOS } from "@/lib/push";

const btn = "px-3 py-1.5 rounded-full text-xs transition-colors";

export const CalendarSyncCard = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["calendar-link"], queryFn: api.calendarLink });
  const token = data?.token;
  const https = token ? `${process.env.REACT_APP_BACKEND_URL}/api/calendar/${token}.ics` : "";
  const webcal = https.replace(/^https?:\/\//, "webcal://");
  const google = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`;
  const set = (t) => qc.setQueryData(["calendar-link"], { token: t });
  const create = async (fresh) => {
    if (fresh && !window.confirm("Make a new link? Calendars using the old link will stop updating.")) return;
    try { set((await api.calendarLinkCreate()).token); } catch { toast.error("Couldn't turn on calendar sync"); }
  };
  const stop = async () => {
    if (!window.confirm("Stop syncing? Your trips will stop updating in your calendar.")) return;
    try { await api.calendarLinkStop(); set(""); toast.success("Calendar sync stopped"); } catch { toast.error("Couldn't stop syncing"); }
  };
  const copy = () => navigator.clipboard.writeText(https).then(() => toast.success("Calendar link copied"), () => toast.error("Couldn't copy"));

  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="calendar-sync-card">
      <div className="flex items-center gap-2"><CalendarSync size={16} className="text-sky-300" /><h3 className="font-display font-semibold">Calendar sync</h3></div>
      <p className="text-xs text-slate-400 mt-1.5">
        All your dated trips appear in your phone's calendar and update by themselves when you change dates.
        {token && " iPhone checks for changes about every hour, Google every few hours."}
      </p>
      {!token ? (
        <button type="button" onClick={() => create(false)} className={`${btn} mt-3 bg-sky-400 text-[#06121c] font-medium hover:bg-sky-300`} data-testid="calendar-sync-on">Turn on calendar sync</button>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mt-3">
            <a href={webcal} className={`${btn} bg-sky-400 text-[#06121c] font-medium hover:bg-sky-300 ${isIOS() ? "order-first" : ""}`} data-testid="calendar-sync-apple">Add to iPhone / Mac calendar</a>
            <a href={google} target="_blank" rel="noreferrer" className={`${btn} border border-white/15 text-slate-200 hover:bg-white/5`} data-testid="calendar-sync-google">Add to Google Calendar</a>
            <button type="button" onClick={copy} className={`${btn} border border-white/15 text-slate-200 hover:bg-white/5 flex items-center gap-1`} data-testid="calendar-sync-copy"><Copy size={12} /> Copy link</button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Anyone with this link can see your trip dates and the names of people you know there (no phone numbers).</p>
          <div className="flex gap-3 mt-2 text-xs">
            <button type="button" onClick={() => create(true)} className="text-slate-400 hover:text-slate-200" data-testid="calendar-sync-new">Make a new link</button>
            <button type="button" onClick={stop} className="text-red-300 hover:text-red-200" data-testid="calendar-sync-off">Stop syncing</button>
          </div>
        </>
      )}
    </section>
  );
};
